// TEN.4 -- el catalogo de planes de clinicas, leido de agency. Fuente UNICA en el frontend.
//
// 🔴 Los precios y los limites NO se escriben aqui: salen de `vertical_plans` por
//    `GET /api/v1/plans/clinicas` (publico, solo planes a la venta; CORS permite
//    clinicas.surit.ai -- medido en TEN.4). La landing llevaba sus precios escritos a mano y
//    vendia planes retirados a precios que ya no existian (DT.136); el boton de pago daba 502.
// 🔴 Si el catalogo no responde, NO hay precios de respaldo: se dice que no se pudieron cargar.
//    Un precio de respaldo escrito aqui seria otra vez una copia que envejece.

import { useCallback, useEffect, useState } from "react";
import { api } from "./api";
import { useAuth } from "../contexts/AuthContext";

export const CATALOGO_PLANES_URL =
  "https://api-scrock-prod.delightfulglacier-4488b9be.westus.azurecontainerapps.io/api/v1/plans/clinicas";

export async function cargarPlanes() {
  const r = await fetch(CATALOGO_PLANES_URL);
  if (!r.ok) throw new Error(`catalogo ${r.status}`);
  const planes = await r.json();
  return [...planes].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
}

// TEN.4: lo que vive en ESTE repo y no en el catalogo de agency: el correo de contacto (una sola
// constante en el backend, `email_templates.CONTACTO_EMAIL`) y el tope de usuarios del panel por
// plan (`plan_limites_colaboradores`, en BD). `{contacto_email, colaboradores_por_plan}`.
export async function cargarDatosAlta() {
  const { data } = await api.get("/publico/alta");
  return data;
}

// Hook: `{estado: "cargando"|"ok"|"error", datos, reintentar}`. El contacto (correo y WhatsApp de
// la conexion acompañada) NO se escribe en el frontend: sale de aqui. Quien lo use decide que
// hacer si no llega -- nunca inventar un valor de respaldo.
export function useDatosAlta() {
  const [st, setSt] = useState({ estado: "cargando", datos: null });
  const reintentar = useCallback(() => {
    setSt({ estado: "cargando", datos: null });
    cargarDatosAlta()
      .then((datos) => setSt({ estado: "ok", datos }))
      .catch(() => setSt({ estado: "error", datos: null }));
  }, []);
  useEffect(() => { reintentar(); }, [reintentar]);
  return { ...st, reintentar };
}

// El catalogo, pedido UNA vez por carga del panel y compartido por todos los componentes que
// preguntan si el plan tiene Sofi (menu, rutas, globo, Dashboard...). Si falla, se olvida la
// promesa para que el siguiente intento vuelva a preguntar.
let _catalogoPromesa = null;
function catalogoCompartido() {
  if (!_catalogoPromesa) {
    _catalogoPromesa = cargarPlanes().catch((e) => { _catalogoPromesa = null; throw e; });
  }
  return _catalogoPromesa;
}

// 🔴 TEN.4 -- LA fuente de "¿este plan tiene a Sofi?" en el panel. Un plan Sistema no ve nada de
//    Sofi. Todo lo que se oculta por plan lee ESTE hook: dos criterios serian dos respuestas.
//    `{estado: "cargando"|"ok"|"error", plan, sofi, sinSofi}`:
//      · plan del catalogo -> `limits.sofi`;
//      · plan que NO esta en el catalogo (los antiguos: starter/pro/growth) -> con Sofi, como
//        hasta hoy: esas clinicas lo tienen contratado;
//      · catalogo caido -> con Sofi: no se esconde nada por una averia.
//    `sinSofi` es true SOLO cuando se sabe que el plan no la incluye. Esconder no es un permiso
//    (DT.71): el backend no cambia.
export function usePlanActual() {
  const { activeClinic } = useAuth();
  const slug = activeClinic?.plan_slug;
  const [st, setSt] = useState({ estado: "cargando", plan: null, sofi: null });
  useEffect(() => {
    if (!slug) return undefined;
    let vivo = true;
    setSt({ estado: "cargando", plan: null, sofi: null });
    catalogoCompartido()
      .then((lista) => {
        const plan = lista.find((x) => x.slug === slug) || null;
        if (vivo) setSt({ estado: "ok", plan, sofi: plan ? tieneSofi(plan) : true });
      })
      .catch(() => vivo && setSt({ estado: "error", plan: null, sofi: true }));
    return () => { vivo = false; };
  }, [slug]);
  return { ...st, sinSofi: st.estado === "ok" && st.sofi === false };
}

// Un plan "tiene Sofi" si su limite lo dice. Es lo que decide si hay WhatsApp en el panel.
export function tieneSofi(plan) {
  return Boolean(plan?.limits?.sofi);
}

export function formatoPrecio(plan) {
  return `$${Number(plan?.price_monthly || 0).toLocaleString("es-MX")}`;
}

// ═══════════════════════════════════════════════════════════════════════════════
// PRECIOS.1 I2 (11 Oct 2026) -- cómo se pinta un precio según el campo `iva` de agency en
// `GET /api/v1/plans/clinicas` (`iva: {modo: "incluido" | "mas_iva", tasa: 0.16}`).
//   · `incluido` (hoy): EXACTAMENTE lo de antes de I2.
//   · `mas_iva`: el importe publicado es la base y lleva «+ IVA»; el cobro con IVA lo enseña Stripe.
// 🔴 Sin el campo (API anterior, caché) o con un valor desconocido: `incluido`. Es la ÚNICA lectura
//    de `iva.modo` en el frontend.
// ═══════════════════════════════════════════════════════════════════════════════
export const MODO_IVA_POR_DEFECTO = "incluido";

export function modoIva(dato) {
  const iva = dato?.iva;
  return iva && typeof iva === "object" && iva.modo === "mas_iva" ? "mas_iva" : MODO_IVA_POR_DEFECTO;
}

// Agency manda el mismo `iva` en todos los planes; si alguno dijera `mas_iva`, gana: decir «IVA
// incluido» cuando el cobro lo suma es el error que no se puede tener.
export function modoDeCatalogo(lista) {
  if (!Array.isArray(lista)) return MODO_IVA_POR_DEFECTO;
  return lista.some((p) => modoIva(p) === "mas_iva") ? "mas_iva" : MODO_IVA_POR_DEFECTO;
}

// Para lo que no viene en el catálogo (paquete de recarga de `/api/consumo`, que no trae `iva`):
// su propio `iva` si algún día lo trae; si no, el del catálogo.
export function modoConRespaldo(dato, modoCatalogo) {
  const iva = dato?.iva;
  if (iva && typeof iva === "object" && "modo" in iva) return modoIva(dato);
  return modoCatalogo === "mas_iva" ? "mas_iva" : MODO_IVA_POR_DEFECTO;
}

export function sufijoIva(modo) {
  return modo === "mas_iva" ? " + IVA" : "";
}

// Texto ya formateado + su sufijo: «MXN/mes» -> «MXN/mes + IVA».
export function conIva(texto, modo) {
  return texto + sufijoIva(modo);
}

// Dado un plan del catálogo: `{importe: "$1,499", modo, sufijoIva: "" | " + IVA"}`. El importe es
// siempre el publicado (`price_monthly`), nunca otro.
export function precioDelPlan(plan) {
  const modo = modoIva(plan);
  return { importe: formatoPrecio(plan), modo, sufijoIva: sufijoIva(modo) };
}

// Textos de clinicas.surit.ai que dependen del modo. Los `incluido` son los de producción antes de
// I2, carácter a carácter; los `mas_iva`, los del diseño PRECIOS.1 §3.
export const TEXTOS_IVA = {
  landingPrecios: {
    incluido: "Sin costo de instalación. Precios mensuales en pesos, con IVA incluido.",
    mas_iva: "Sin costo de instalación. Precios mensuales en pesos, más IVA.",
  },
  tarjeta: { incluido: "IVA incluido", mas_iva: "más IVA" },
  modal: {
    incluido: (precio) => `${precio} MXN/mes, IVA incluido · Pago seguro vía Stripe`,
    mas_iva: (precio) => `${precio} MXN/mes + IVA · Pago seguro vía Stripe`,
  },
  // Botón de pago: nunca un importe distinto del que cobrará Stripe sin decirlo: con `mas_iva`,
  // la base va seguida de «+ IVA».
  botonPagar: {
    incluido: (precio) => `Ir a pagar · ${precio}/mes`,
    mas_iva: (precio) => `Ir a pagar · ${precio} + IVA/mes`,
  },
};

export function textoIva(clave, modo, ...args) {
  const t = TEXTOS_IVA[clave][modo === "mas_iva" ? "mas_iva" : MODO_IVA_POR_DEFECTO];
  return typeof t === "function" ? t(...args) : t;
}

// Hook: modo del catálogo para lo que no tiene un plan a mano (la recarga del panel). Una sola
// petición compartida con `usePlanActual`; mientras carga o si falla: `incluido`.
export function useModoIva() {
  const [modo, setModo] = useState(MODO_IVA_POR_DEFECTO);
  useEffect(() => {
    let vivo = true;
    catalogoCompartido()
      .then((lista) => { if (vivo) setModo(modoDeCatalogo(lista)); })
      .catch(() => {});
    return () => { vivo = false; };
  }, []);
  return modo;
}

// Los renglones de cada tarjeta se DERIVAN de `limits`: ningun renglon afirma algo que el plan
// no tenga en la base. "Profesionales" es la SUMA de medicos y prestadores (decision de TEN.4),
// y se dice asi -- nunca "1 medico + 2 prestadores".
// `colaboradores` = el mapa `colaboradores_por_plan` de `cargarDatosAlta()`; si no llego, el
// renglon de usuarios del panel se omite en vez de inventarse.
export function rasgosDelPlan(plan, colaboradores) {
  const l = plan?.limits || {};
  const out = [];
  out.push(l.max_doctors === -1 || l.max_doctors == null
    ? "Profesionales sin límite"
    : `Hasta ${l.max_doctors} profesionales (médicos y prestadores)`);
  if (colaboradores && plan?.slug in colaboradores) {
    const n = colaboradores[plan.slug];
    out.push(n == null ? "Usuarios del panel sin límite" : `Hasta ${n} usuarios del panel`);
  }
  out.push("Agenda, pacientes, cobros, gastos y reportes");
  if (tieneSofi(plan)) {
    const conv = l.max_conversations_per_month;
    out.push(conv === -1 || conv == null
      ? "Sofi atiende tu WhatsApp, con volumen a la medida"
      : `Sofi atiende tu WhatsApp: hasta ${conv.toLocaleString("es-MX")} conversaciones al mes`);
    // K1.6: el Job solo recuerda citas agendadas con >= 2 dias de anticipacion.
    out.push("Recordatorio por WhatsApp el día anterior, en citas agendadas con al menos dos días de anticipación");
    out.push("Conexión de WhatsApp acompañada");
  } else {
    out.push("Sin WhatsApp ni asistente");
  }
  return out;
}
